import React, {useEffect, useState} from "react";
import {useTranslation} from "@unibuddy/intl";
import {Box, Stack} from "@unibuddy/patron";
import ChatNowButton from "shared/Blog/components/ChatNowButton/ChatNowButton";
import ThemedLink from "shared/Navigation/components/ThemedLink/ThemedLink";
import MentorCardBanner from "shared/Mentor/components/MentorCardBanner/MentorCardBanner";
import MentorCardDegree from "shared/Mentor/components/MentorCardDegree/MentorCardDegree";
import MentorCardName from "shared/Mentor/components/MentorCardName/MentorCardName";
import MentorCardSeparator from "shared/Mentor/components/MentorCardSeparator/MentorCardSeparator";
import {isFeatureEnabled} from "shared/Utils/FeatureUtils";

import MentorComponent from "./MentorComponent";
import {MentorField, UniversityField} from "../../../../../types/generated";
import s from "./MentorPreviewCard.pcss";

type PreviewCardTypes = {
    mentor: MentorField;
    university: UniversityField;
    rootUrl: string;
    buddyPosition: number;
    universityName: string;
    isMatched: boolean;
    onAvatarClicked: (mentorId: string) => null;
    onChatNow: (mentorId: string, position: string) => null;
    onSeeMoreClicked: (mentorId: string) => null;
};

const TopRightComp = ({isMatched = false, degreeLevel = ""}): JSX.Element => {
    if (isMatched && degreeLevel === "Alumni") {
        return (
            <div data-test-id="both">
                <i className="fa fa-star fa-lg" aria-hidden="true" />
                &nbsp; Alumni
            </div>
        );
    } else if (isMatched) {
        return <i data-test-id="star" className="fa fa-star fa-lg" aria-hidden="true" />;
    } else if (degreeLevel === "Alumni") {
        return <>Alumni</>;
    }
    return null;
};

const MentorPreviewCard = ({
    mentor,
    university,
    onAvatarClicked,
    rootUrl,
    buddyPosition,
    isMatched,
    universityName,
    onChatNow,
    onSeeMoreClicked,
}: PreviewCardTypes) => {
    const [orderedMentorField, setOrderedMentorField] = useState<string[]>([]);
    const {t} = useTranslation("mentorCard");
    const isDegreeTypesEnabled = isFeatureEnabled("enableDegreeTypes", university);
    useEffect(() => {
        const mentorFieldOrder = university?.mentorFieldOrder ?? [];
        mentorFieldOrder.sort((field1, field2) => field1.order - field2.order);
        setOrderedMentorField(mentorFieldOrder.map((mentorField) => mentorField.field));
    }, [university]);

    const mentorName = mentor.firstName ?? "";
    const mentorId = mentor.id;

    const params = new URLSearchParams(window.location.search);
    params.set("buddyPosition", buddyPosition.toString());

    return (
        <Box
            maxW="400px"
            minH="455px"
            borderColor="#e4e8eb"
            borderWidth={1}
            borderRadius={"xsmall"}
            backgroundColor="white"
            display={"flex"}
            flexDirection={"column"}
        >
            <MentorCardBanner
                name={mentorName}
                className={s.banner}
                lastSeen={mentor.lastSeen ?? ""}
                profilePhoto={mentor.profilePhoto}
                imageLink={`${rootUrl}/buddies/students/${mentorId}?${params.toString()}`}
                onImageClicked={() => onAvatarClicked(mentorId)}
                topRight={<TopRightComp isMatched={isMatched} degreeLevel={mentor.degreeLevel} />}
            />

            <MentorCardName name={mentorName} />

            {!isDegreeTypesEnabled ? <MentorCardDegree degree={mentor.degree?.name ?? ""} /> : null}

            {universityName ? (
                <p className={s.university} data-test-id="university">
                    {universityName}
                </p>
            ) : null}

            <div className={s.chatNow}>
                <ChatNowButton
                    onChatNow={onChatNow}
                    name={mentorName}
                    className={s.chatNowButton}
                    id={mentorId}
                    large={false}
                />
            </div>

            <MentorCardSeparator />
            <Box maxH={"300px"} overflow={"hidden"} paddingBottom={"medium"}>
                {orderedMentorField.map((field) => {
                    return (
                        <Stack key={field} space={"small"}>
                            {MentorComponent(field, {
                                university,
                                mentor,
                                t,
                            })}
                        </Stack>
                    );
                })}
            </Box>

            <Box marginTop={"auto"} paddingTop={"small"}>
                <div className={s.seeMore}>
                    <ThemedLink
                        id={`read-more-${mentorId}`}
                        className={s.seeMoreLink}
                        onClick={() => onSeeMoreClicked(mentorId)}
                        to={`${rootUrl}/buddies/students/${mentorId}?buddyPosition=${buddyPosition}`}
                        external={false}
                    >
                        {t("readMore", `Read more about ${mentorName} ...`, {
                            name: mentorName,
                        })}
                    </ThemedLink>
                </div>
            </Box>
        </Box>
    );
};

export default MentorPreviewCard;
